import moment from 'moment'
import {
  TextInput,
  EmailInput,
  PhoneNumberInput,
  DateInput,
  LevelInput,
  LocaleInput,
  BasicInputGroup,
} from 'components/UserInputs'
import { RadioInputGroup } from 'components/UserInputs/RadioInputGroup'
import {
  TEACHER_UPDATE,
  STUDENT_UPDATE,
  GENDER_OPTIONS,
  PHONE_TYPE_OPTIONS,
  ANDROID,
} from '../../constants'
import { useFormikContext } from 'formik'
import { Box, IconButton } from 'theme-ui'
import {
  AnyUserData,
  ConfigUtils,
  FormikOnlyValues,
  PageContext,
  StatusType,
  ViewMode,
} from 'types'
import { Tooltip } from 'components/Tooltip'
import { InfoIcon } from 'components/Icon'

const maxMoment = moment().subtract(16, 'years')
const maxDate = {
  year: maxMoment.year(),
  month: maxMoment.month() + 1,
  day: maxMoment.date(),
}

export type BasicUserFields =
  | 'countryCode'
  | 'forename'
  | 'surname'
  | 'email'
  | 'phone'
  | 'dateOfBirth'
  | 'gender'
  | 'device'
  | 'googlePlayEmail'
  | 'level'
  | 'source'
  | 'campaignName'
  | 'orgName'
  | 'createdAt'
  | 'appSignupDate'
  | 'locale'

export type BasicUserFormValues = Pick<AnyUserData, BasicUserFields> & {
  status?: StatusType
}

export const BasicUserSection = ({
  isSupport,
  viewMode,
  configUtils,
  isTeacherTraining,
  isDisabledField,
  shouldShowError = () => true,
  startTabIndex,
  pageContext,
}: {
  isSupport: boolean
  viewMode: ViewMode
  configUtils: ConfigUtils
  isTeacherTraining?: boolean
  isDisabledField: (fieldName: keyof BasicUserFormValues) => boolean
  shouldShowError?: (fieldName: keyof BasicUserFormValues) => boolean
  startTabIndex: number
  pageContext: PageContext
}) => {
  const { values, errors, touched, setFieldValue, handleBlur, handleChange } =
    useFormikContext<BasicUserFormValues & FormikOnlyValues>()

  const testEmailAlert =
    'For test students the email address cannot be edited. The student emails will automatically be sent to you.'
  return (
    <>
      {viewMode === 'edit' && (
        <>
          <Box>
            <TextInput
              name="forename"
              label="First name"
              value={values.forename}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isDisabledField('forename')}
              error={shouldShowError('forename') ? errors.forename : undefined}
              tabIndex={startTabIndex}
            />
          </Box>
          <Box>
            <TextInput
              name="surname"
              label="Last name"
              value={values.surname}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isDisabledField('surname')}
              error={shouldShowError('surname') ? errors.surname : undefined}
              tabIndex={startTabIndex + 1}
            />
          </Box>
        </>
      )}

      <Box>
        <EmailInput
          name="email"
          label="Email"
          value={values.email}
          setFieldValue={setFieldValue}
          disabled={isTeacherTraining ? true : isDisabledField('email')}
          error={
            isTeacherTraining
              ? testEmailAlert
              : shouldShowError('email')
              ? errors.email
              : undefined
          }
          onBlur={handleBlur}
          tabIndex={startTabIndex + 2}
        />
      </Box>

      {pageContext === TEACHER_UPDATE ? (
        <Box />
      ) : (
        <>
          {configUtils.isCountryBeta(values.countryCode) ? (
            <Box>
              <Box>
                <RadioInputGroup
                  label="Phone type"
                  value={values.device}
                  options={PHONE_TYPE_OPTIONS}
                  name="device"
                  error={shouldShowError('device') ? errors.device : undefined}
                  setFieldValue={setFieldValue}
                  onBlur={handleBlur}
                  disabled={isDisabledField('device')}
                  tabIndex={startTabIndex + 6}
                />
              </Box>
              {values.device === ANDROID && (
                <Box>
                  <EmailInput
                    name="googlePlayEmail"
                    label="Google play email"
                    value={values.googlePlayEmail}
                    onBlur={handleBlur}
                    disabled={isDisabledField('device')}
                    error={
                      shouldShowError('googlePlayEmail') &&
                      touched.googlePlayEmail
                        ? errors.googlePlayEmail
                        : undefined
                    }
                    tabIndex={startTabIndex + 7}
                    setFieldValue={setFieldValue}
                  />
                </Box>
              )}
            </Box>
          ) : null}
          <Box>
            <PhoneNumberInput
              name="phone"
              label="Phone number"
              value={values.phone}
              setFieldValue={setFieldValue}
              disabled={isDisabledField('phone')}
              error={shouldShowError('phone') ? errors.phone : undefined}
              countryCode={values.countryCode}
              onBlur={handleBlur}
              tabIndex={startTabIndex + 3}
            />
          </Box>
          <Box>
            <DateInput
              label="Date of birth"
              name="dateOfBirth"
              value={values.dateOfBirth}
              disabled={isDisabledField('dateOfBirth')}
              setFieldValue={setFieldValue}
              error={
                shouldShowError('dateOfBirth') ? errors.dateOfBirth : undefined
              }
              onBlur={handleBlur}
              tabIndex={startTabIndex + 4}
              maxDate={maxDate}
            />
          </Box>
          <Box>
            <LocaleInput
              name="locale"
              label="Language"
              value={values.locale || 'en-US'}
              setFieldValue={setFieldValue}
              disabled={isDisabledField('locale')}
              error={shouldShowError('locale') ? errors.locale : undefined}
              onBlur={handleBlur}
              tabIndex={startTabIndex + 5}
            />
          </Box>
        </>
      )}
      {pageContext === STUDENT_UPDATE && (
        <>
          {!!(values.gender || isSupport) && (
            <Box>
              <RadioInputGroup
                options={GENDER_OPTIONS}
                value={values.gender}
                name="gender"
                label="Gender"
                error={shouldShowError('gender') ? errors.gender : undefined}
                setFieldValue={setFieldValue}
                onBlur={handleBlur}
                disabled={isDisabledField('gender')}
                tabIndex={startTabIndex + 5}
              />
            </Box>
          )}
          {values.status !== 'locked' && (
            <Box>
              <LevelInput
                label="Level"
                name="level"
                value={values.__level}
                setFieldValue={setFieldValue}
                disabled={isDisabledField('level')}
                error={shouldShowError('level') ? errors.level : undefined}
                onBlur={handleBlur}
                tabIndex={startTabIndex}
              />
            </Box>
          )}
          {isSupport && (
            <Box>
              <BasicInputGroup
                name={'metadata'}
                label={'Creation data'}
                display={
                  <>
                    <Box>
                      Source:{' '}
                      {values.source === 'api'
                        ? `${values.orgName} API`
                        : 'Manual'}
                    </Box>
                    {values.campaignName ? (
                      <Box>Campaign: {values.campaignName}</Box>
                    ) : null}
                    {values.createdAt ? (
                      <Box>
                        Created: {new Date(values.createdAt).toDateString()}
                      </Box>
                    ) : (
                      <Box></Box>
                    )}
                  </>
                }
                disabled={true}
                input={<Box />}
              />
            </Box>
          )}
          <Box>
            <BasicInputGroup
              name={'signupDate'}
              label={'Successfully logged in'}
              display={
                <>
                  <Box>
                    {values.appSignupDate ? (
                      <>
                        <Box
                          variant="forms.inputGroup.fullWidth"
                          sx={{ display: 'inline-block', flex: '1 0 100%' }}
                        >
                          Yes
                        </Box>
                        <Tooltip
                          variant="appSignupDate"
                          width={240}
                          tooltipSx={{ left: 0 }}
                        >
                          <IconButton
                            type="button"
                            variant={`icon.primary`}
                            sx={{ outline: 'none' }}
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Box>No</Box>
                    )}
                  </Box>
                </>
              }
              disabled={true}
              input={<Box />}
            />
          </Box>
        </>
      )}
    </>
  )
}
